import {
  Avatar,
  Box,
  MenuItem,
  Select,
  SelectProps,
  Typography,
} from '@mui/material'
import { colorPalette } from '../../../style/colorPalette'
import { intl } from '../../../../i18n'
import TicketListApi, {
  TicketListDetail,
} from '../../../../lib/functions/ticketList'
import { useProjectPrivateContext } from '../../../context/projectContext'
import { projectPrivate } from '../../../higher-order-components/projectPrivate'
import { useCallback, useEffect, useState, ReactNode } from 'react'
import { WbsItemTypeVO } from '../../../../domain/value-object/WbsItemTypeVO'

type Props = {
  ticketType?: string
  onChangeTicketType?: (value?: string) => void
  ticketListUuid: string | undefined
  onChangeTicketList: (value: TicketListDetail | undefined) => void
}

type StyledSelectProps = Omit<SelectProps, 'sx' | 'MenuProps'> & {
  children: ReactNode
}

const StyledSelect = ({ children, ...props }: StyledSelectProps) => (
  <Select
    {...props}
    sx={{
      border: `1px solid ${colorPalette.monotone[2]}`,
      borderRadius: '4px',
      width: '370px',
      height: '40px',
      color: colorPalette.monotone[4],
      backgroundColor: colorPalette.monotone[0],
    }}
    MenuProps={{
      sx: {
        width: '370px',
        height: '540px',
        fontSize: '14px',
        fontWeight: 700,
        lineHeight: '21px',
      },
    }}
  >
    {children}
  </Select>
)

export const TicketListSelector = projectPrivate(
  ({
    ticketType,
    onChangeTicketType,
    ticketListUuid,
    onChangeTicketList,
  }: Props) => {
    const { projectUuid, ticketTypes } = useProjectPrivateContext()
    const [ticketLists, setTicketLists] = useState<TicketListDetail[]>([])

    const fetchTicketLists = useCallback(async () => {
      let lists: TicketListDetail[] = []
      if (projectUuid && ticketType) {
        const response = await TicketListApi.getAll({
          ticketType: ticketType,
        })
        lists = response.json
      }
      setTicketLists(lists)
      const list = lists.find(v => v.uuid === ticketListUuid)
      onChangeTicketList(list)
    }, [projectUuid, ticketType, ticketListUuid, onChangeTicketList])

    const onChangeTicketListUuid = useCallback(
      event => {
        const targetUuid = event.target.value
        const targetDetail = ticketLists.find(v => v.uuid === targetUuid)
        onChangeTicketList(targetDetail)
      },
      [ticketLists, onChangeTicketList]
    )

    useEffect(() => {
      fetchTicketLists()
    }, [projectUuid, ticketType, ticketListUuid])

    return (
      <Box
        sx={{
          margin: '10px 10px 0',
          padding: '10px',
          backgroundColor: '#D8DDE54D',
          display: 'flex',
          flexFlow: 'row',
          gap: '8px',
        }}
      >
        {onChangeTicketType && (
          <Box sx={{ display: 'flex', flexFlow: 'column' }}>
            <Typography
              sx={{ minWidth: '128px', color: colorPalette.monotone[4] }}
            >
              {intl.formatMessage({ id: 'ticketType' })}
            </Typography>
            <StyledSelect
              value={ticketType || ''}
              onChange={e => onChangeTicketType(e.target.value as string)}
              renderValue={value => {
                const selected = ticketTypes.find(v => v.code === value)
                if (!selected) return ''
                return <TicketTypeDisplay ticketTypeVO={selected} />
              }}
            >
              {ticketTypes.map(v => (
                <MenuItem key={v.code} value={v.code}>
                  <TicketTypeDisplay ticketTypeVO={v} isMenuItem={true} />
                </MenuItem>
              ))}
            </StyledSelect>
          </Box>
        )}
        <Box sx={{ display: 'flex', flexFlow: 'column' }}>
          <Typography
            sx={{ minWidth: '128px', color: colorPalette.monotone[4] }}
          >
            {intl.formatMessage({ id: 'ticketList' })}
          </Typography>
          <StyledSelect
            value={ticketListUuid || ''}
            onChange={onChangeTicketListUuid}
          >
            {ticketLists.map(v => {
              return (
                <MenuItem
                  key={v.uuid}
                  value={v.uuid}
                  sx={{
                    color: colorPalette.monotone[4],
                    fontSize: '14px',
                    fontWeight: 500,
                    lineHeight: '21px',
                  }}
                >
                  {v.wbsItem.displayName}
                </MenuItem>
              )
            })}
          </StyledSelect>
        </Box>
      </Box>
    )
  }
)

const TicketTypeDisplay = ({
  ticketTypeVO,
  isMenuItem = false,
}: {
  ticketTypeVO: WbsItemTypeVO
  isMenuItem?: boolean
}) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
    <Avatar
      variant={'circular'}
      src={ticketTypeVO.iconUrl}
      sx={{ width: '20px', height: '20px' }}
    />
    <Typography
      sx={{
        color: isMenuItem ? colorPalette.monotone[4] : undefined,
        fontSize: '14px',
        fontWeight: 500,
        lineHeight: '20px',
      }}
    >
      {ticketTypeVO.name}
    </Typography>
  </Box>
)
